import { CommerceType } from 'types/models/company';
import { SettingsMain, SettingsPageHeader } from '../SettingsLayout';
import { useAppSelector } from 'store/index';
import { selectCompany, selectCompanyId } from 'store/selectors/me/company';
import CommerceTypeAvatar from 'storybookConfig/stories/cells/Avatar/CommerceTypeAvatar';
import { Controller, useForm } from 'react-hook-form';
import type { MarketplaceFormData } from 'containers/MarketplaceOnboardingPage/types';
import Form from 'storybookConfig/stories/organisms/Form';
import Input from 'storybookConfig/stories/molecules/Input';
import Flex from '@react-css/flex';
import styled from '@emotion/styled';
import Divider from 'storybookConfig/stories/molecules/Divider';
import CategorySelector from 'containers/MarketplaceOnboardingPage/CategorySelector';
import Body from 'storybookConfig/stories/molecules/Body';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { useState } from 'react';
import { uploadPublicImage } from 'utils/api/documents';
import useAlertQueue from 'hooks/useAlertQueue';
import Icon from 'storybookConfig/stories/molecules/Icon';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import Toggle from 'storybookConfig/stories/molecules/Toggle';
import { useMutation } from '@tanstack/react-query';
import { updateCompany } from 'utils/api/companies';
import type { Company } from 'types/models/company';

const TopAlignedTextArea = styled.textarea`
  outline: 0;
  max-width: 100%;
  min-height: 135px;
  height: auto;
  border-radius: 16px;
  padding: 10px 16px;
  border: 1px solid transparent;
  background: ${({ theme }) => theme.color.gray100};
  width: 100%;
  resize: vertical;

  &:active,
  &:focus {
    border-color: ${({ theme }) => theme.color.gray300};
  }

  &:not(:placeholder-shown) {
    border-color: ${({ theme }) => theme.color.blue400};
  }

  &::placeholder {
    color: ${({ theme }) => theme.color.bodyTextSecondary};
  }
`;

const StyledPhoneInput = styled.div`
  width: 100%;

  .react-international-phone-input-container {
    background: ${({ theme }) => theme.color.gray100};
    border-radius: 23px;
    padding: 0 16px;
    min-height: 46px;
    display: flex;
    align-items: center;
    border: 1px solid transparent;
    width: 100%;

    &:focus-within {
      border-color: ${({ theme }) => theme.color.gray300};
    }

    .react-international-phone-country-selector-button {
      background: transparent;
      border: none;
      padding: 0 8px 0 0;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .react-international-phone-input {
      background: transparent;
      border: none;
      padding: 0;
      width: 100%;
      outline: none;
      color: ${({ theme }) => theme.color.bodyTextPrimary};
      font-size: 16px;

      &::placeholder {
        color: ${({ theme }) => theme.color.bodyTextSecondary};
      }
    }
  }

  &.invalid .react-international-phone-input-container {
    border-color: ${({ theme }) => theme.color.error500};
  }
`;

const ChangeImageLabel = styled.label`
  width: 100%;
  cursor: pointer;
`;

const ChangeImageButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.color.blue500};
  color: ${({ theme }) => theme.color.blue500};
  border-radius: 23px;
  cursor: pointer;
`;

const UploadImageLabel = styled.label`
  width: 100%;
  cursor: pointer;
`;

const UploadImageInput = styled.input`
  display: none;
`;

const UploadImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100px;
  border: 1px dashed;
  border-color: ${({ theme }) => theme.color.gray400};
  border-radius: 10px;
  background-color: ${({ theme }) => theme.color.gray100};
  cursor: pointer;
`;

const UploadedImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.color.blue500};
`;

const UploadedImage = styled.img`
  width: 65px;
  height: 65px;
  border-radius: 8px;
  object-fit: cover;
  object-position: center;
`;

type FormFields = Pick<
  MarketplaceFormData,
  | 'yearEstablished'
  | 'location'
  | 'brandDescription'
  | 'primaryCategories'
  | 'secondaryCategories'
  | 'primaryContact'
  | 'secondaryContact'
  | 'nsfwProducts'
>;

const BrandSettings = () => {
  const company = useAppSelector(selectCompany);
  const companyId = useAppSelector(selectCompanyId);
  const { commerceType } = company ?? {};
  const { addErrorAlert, addSuccessAlert } = useAlertQueue();
  const [primaryPhoto, setPrimaryPhoto] = useState<string | null>(
    company?.marketplaceSettings?.primaryContact?.profile || null
  );
  const [secondaryPhoto, setSecondaryPhoto] = useState<string | null>(
    company?.marketplaceSettings?.secondaryContact?.profile || null
  );

  const { control, handleSubmit, register, formState, watch, setValue } = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: {
      yearEstablished: company?.marketplaceSettings?.yearEstablished,
      location: company?.marketplaceSettings?.location,
      brandDescription: company?.marketplaceSettings?.brandDescription,
      primaryCategories: company?.marketplaceSettings?.primaryCategories,
      secondaryCategories: company?.marketplaceSettings?.secondaryCategories,
      primaryContact: company?.marketplaceSettings?.primaryContact,
      secondaryContact: company?.marketplaceSettings?.secondaryContact,
      nsfwProducts: company?.marketplaceSettings?.nsfwProducts || false,
    },
  });

  const formValues = watch();

  const onLogoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    type: 'primary' | 'secondary'
  ) => {
    if (!event.target.files) return;

    try {
      const file = event.target.files[0];
      if (file) {
        // Check file size (5MB limit)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
          addErrorAlert('File too large', 'Please select an image smaller than 5MB');
          return;
        }

        await uploadPublicImage(file, {
          onPreview: (url) => {
            if (type === 'primary') {
              setPrimaryPhoto(url);
            } else {
              setSecondaryPhoto(url);
            }
          },
          onComplete: (url) => {
            if (type === 'primary') {
              setPrimaryPhoto(url);
            } else {
              setSecondaryPhoto(url);
            }
          },
          onError: (error) => {
            console.error('Error uploading image', error);
            addErrorAlert('Something went wrong', 'Unable to upload image');
          },
        });
      }
    } catch (error) {
      console.error('Error uploading image', error);
      addErrorAlert('Something went wrong', 'Unable to upload image');
    }
  };

  const { mutate: updateBrandSettings, isLoading } = useMutation<Company, Error, FormFields>({
    mutationFn: (data: FormFields) => {
      const updatedData: MarketplaceFormData = {
        yearEstablished: Number(data.yearEstablished),
        location: data.location,
        brandDescription: data.brandDescription,
        primaryCategories: data.primaryCategories,
        secondaryCategories: data.secondaryCategories,
        nsfwProducts: data.nsfwProducts,
        primaryContact: {
          ...data.primaryContact,
          profile: primaryPhoto || '',
        },
        secondaryContact: {
          ...data.secondaryContact,
          phoneNumber:
            data.secondaryContact?.phoneNumber && data.secondaryContact.phoneNumber.length < 4
              ? ''
              : data.secondaryContact?.phoneNumber,
          profile: secondaryPhoto || '',
        },
      };

      return updateCompany(companyId ?? '', {
        marketplaceSettings: updatedData,
      });
    },
    onSuccess: () => {
      addSuccessAlert(
        'Brand Settings Updated',
        'Your brand settings have been saved successfully.'
      );
    },
    onError: (error) => {
      addErrorAlert('Something went wrong', error.message);
      console.error('Unable to update brand settings', error);
    },
  });

  const onSubmit = (data: FormFields) => {
    updateBrandSettings(data);
  };

  return (
    <SettingsMain>
      <SettingsPageHeader title="Brand">
        <CommerceTypeAvatar commerceType={commerceType || CommerceType.None} />
      </SettingsPageHeader>
      <h4>About the Brand</h4>
      <p className="text-muted small">
        Information about the brand, all of this information is used in your brand profile inside
        the marketplace.
      </p>
      <Form onSubmit={handleSubmit(onSubmit)} style={{ gap: '16px' }}>
        <Form.FieldGroup>
          <Flex gap="16px">
            <Flex.Item flex="1">
              <Form.Field
                labelText="Year Established"
                id="year-established"
                errorText={formState.errors.yearEstablished?.message}
                isRequired
              >
                <Input
                  {...register('yearEstablished', { required: 'Year Established is required' })}
                  placeholder="2021"
                  isFullWidth
                />
              </Form.Field>
            </Flex.Item>
            <Flex.Item flex="1">
              <Form.Field
                labelText="Location"
                id="location"
                errorText={formState.errors.location?.message}
                isRequired
              >
                <Input
                  {...register('location', { required: 'Location is required' })}
                  placeholder="Location"
                  isFullWidth
                />
              </Form.Field>
            </Flex.Item>
          </Flex>
          <Flex gap="16px">
            <Flex.Item flex="1">
              <Form.Field
                labelText="Brand Description"
                id="brand-description"
                errorText={formState.errors.brandDescription?.message}
              >
                <TopAlignedTextArea
                  {...register('brandDescription')}
                  placeholder="Brand Description"
                />
              </Form.Field>
            </Flex.Item>
          </Flex>
          <div>
            <Flex justifyContent="space-between" alignItems="center">
              <Body variant="Body/Regular">NSFW Products</Body>
              <Controller
                control={control}
                name="nsfwProducts"
                render={({ field }) => (
                  <Toggle
                    name="nsfwProducts"
                    checked={field.value || false}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                )}
              />
            </Flex>
            <Body variant="Body/Body Small" color="bodyTextSecondary">
              Do you sell products that are Not Safe For Work?
            </Body>
          </div>
        </Form.FieldGroup>
        <Divider />
        <h4 style={{ marginBottom: '4px' }}>Categories</h4>
        <p className="text-muted small" style={{ marginTop: '0px', marginBottom: '8px' }}>
          Select the categories that best describe your brand.
        </p>
        <div style={{ marginTop: '8px' }}>
          <Form.FieldGroup>
            <Flex gap="16px">
              <Flex.Item flex="1">
                <Form.Field
                  labelText="Primary Category"
                  id="primary-category"
                  errorText={formState.errors.primaryCategories?.message}
                >
                  <CategorySelector
                    value={(formValues.primaryCategories ?? []).filter(
                      (v: any): v is string => typeof v === 'string'
                    )}
                    onChange={(v) => setValue('primaryCategories', v)}
                    label="Primary Category"
                  />
                </Form.Field>
              </Flex.Item>
              <Flex.Item flex="1">
                <Form.Field
                  labelText="Secondary Category"
                  id="secondary-category"
                  errorText={formState.errors.secondaryCategories?.message}
                >
                  <CategorySelector
                    value={(formValues.secondaryCategories ?? []).filter(
                      (v: any): v is string => typeof v === 'string'
                    )}
                    onChange={(v) => setValue('secondaryCategories', v)}
                    label="Secondary Category"
                  />
                </Form.Field>
              </Flex.Item>
            </Flex>
          </Form.FieldGroup>
        </div>
        <Divider />
        <h4 style={{ marginBottom: '4px' }}>Contact Information</h4>
        <p className="text-muted small" style={{ marginTop: '0px', marginBottom: '8px' }}>
          This is the primary and secondary means of contact for your brand.
        </p>
        <div style={{ marginTop: '8px' }}>
          <Form.FieldGroup>
            <Flex gap="16px">
              <Flex.Item flex="1">
                <div style={{ marginBottom: '10px' }}>
                  <Body variant="Body/Header">Primary Contact</Body>
                </div>
                <Flex gap="16px">
                  <Flex.Item flex="1">
                    <div style={{ marginBottom: '12px' }}>
                      <Form.Field
                        labelText="First Name"
                        id="primary-contact-first-name"
                        errorText={formState.errors.primaryContact?.firstName?.message}
                        isRequired
                      >
                        <Input
                          {...register('primaryContact.firstName', {
                            required: 'First name is required',
                          })}
                          placeholder="First Name"
                          isFullWidth
                        />
                      </Form.Field>
                    </div>
                  </Flex.Item>
                  <Flex.Item flex="1">
                    <div style={{ marginBottom: '12px' }}>
                      <Form.Field
                        labelText="Last Name"
                        id="primary-contact-last-name"
                        errorText={formState.errors.primaryContact?.lastName?.message}
                        isRequired
                      >
                        <Input
                          {...register('primaryContact.lastName', {
                            required: 'Last name is required',
                          })}
                          placeholder="Last Name"
                          isFullWidth
                        />
                      </Form.Field>
                    </div>
                  </Flex.Item>
                </Flex>
                <div style={{ marginBottom: '12px' }}>
                  <Form.Field
                    labelText="Job Title"
                    id="primary-contact-job-title"
                    errorText={formState.errors.primaryContact?.jobTitle?.message}
                    isRequired
                  >
                    <Input
                      {...register('primaryContact.jobTitle', {
                        required: 'Job Title is required',
                      })}
                      placeholder="Senior Manager"
                      isFullWidth
                    />
                  </Form.Field>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <Form.Field
                    labelText="Email"
                    id="primary-contact-email"
                    errorText={formState.errors.primaryContact?.email?.message}
                    isRequired
                  >
                    <Input
                      {...register('primaryContact.email', { required: 'Email is required' })}
                      placeholder="Email"
                      isFullWidth
                    />
                  </Form.Field>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <Form.Field
                    labelText="Phone Number"
                    id="primary-contact-phone-number"
                    errorText={formState.errors.primaryContact?.phoneNumber?.message}
                    isRequired
                  >
                    <Controller
                      name="primaryContact.phoneNumber"
                      control={control}
                      rules={{
                        required: 'Phone number is required',
                        validate: (value) => {
                          if (!value) return 'Phone number is required';
                          const numericValue = value.replace(/\D/g, '');
                          if (numericValue.length < 10) return 'Phone number is too short';
                          if (numericValue.length > 15) return 'Phone number is too long';
                          return true;
                        },
                      }}
                      render={({ field: { onChange, value }, fieldState: { invalid } }) => (
                        <StyledPhoneInput className={invalid ? 'invalid' : ''}>
                          <PhoneInput defaultCountry="us" value={value} onChange={onChange} />
                        </StyledPhoneInput>
                      )}
                    />
                  </Form.Field>
                </div>
                <Form.Field labelText="Primary Contact Photo" id="primary-contact-photo">
                  <UploadImageInput
                    type="file"
                    id="primary-contact-logo-upload"
                    onChange={(e) => onLogoUpload(e, 'primary')}
                  />
                  {primaryPhoto ? (
                    <>
                      <UploadedImageContainer>
                        <UploadedImage src={primaryPhoto} alt="Primary Contact" />
                      </UploadedImageContainer>
                      <ChangeImageLabel htmlFor="primary-contact-logo-upload">
                        <ChangeImageButton>Change Image</ChangeImageButton>
                      </ChangeImageLabel>
                    </>
                  ) : (
                    <UploadImageLabel htmlFor="primary-contact-logo-upload">
                      <UploadImageContainer>
                        <Flex column alignItemsCenter>
                          <Icon name="image" size="30px" color="gray400" />
                          <Body variant="Body/Body Small" color="gray400">
                            Upload Image
                          </Body>
                        </Flex>
                      </UploadImageContainer>
                    </UploadImageLabel>
                  )}
                </Form.Field>
              </Flex.Item>
              <Flex.Item flex="1">
                <div style={{ marginBottom: '10px' }}>
                  <Body variant="Body/Header">Secondary Contact</Body>
                </div>
                <Flex gap="16px">
                  <Flex.Item flex="1">
                    <div style={{ marginBottom: '12px' }}>
                      <Form.Field
                        labelText="First Name"
                        id="secondary-contact-first-name"
                        errorText={formState.errors.secondaryContact?.firstName?.message}
                      >
                        <Input
                          {...register('secondaryContact.firstName')}
                          placeholder="First Name"
                          isFullWidth
                        />
                      </Form.Field>
                    </div>
                  </Flex.Item>
                  <Flex.Item flex="1">
                    <div style={{ marginBottom: '12px' }}>
                      <Form.Field
                        labelText="Last Name"
                        id="secondary-contact-last-name"
                        errorText={formState.errors.secondaryContact?.lastName?.message}
                      >
                        <Input
                          {...register('secondaryContact.lastName')}
                          placeholder="Last Name"
                          isFullWidth
                        />
                      </Form.Field>
                    </div>
                  </Flex.Item>
                </Flex>
                <div style={{ marginBottom: '12px' }}>
                  <Form.Field
                    labelText="Job Title"
                    id="secondary-contact-job-title"
                    errorText={formState.errors.secondaryContact?.jobTitle?.message}
                  >
                    <Input
                      {...register('secondaryContact.jobTitle')}
                      placeholder="Senior Manager"
                      isFullWidth
                    />
                  </Form.Field>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <Form.Field
                    labelText="Email"
                    id="secondary-contact-email"
                    errorText={formState.errors.secondaryContact?.email?.message}
                  >
                    <Input
                      {...register('secondaryContact.email')}
                      placeholder="Email"
                      isFullWidth
                    />
                  </Form.Field>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <Form.Field
                    labelText="Phone Number"
                    id="secondary-contact-phone-number"
                    errorText={formState.errors.secondaryContact?.phoneNumber?.message}
                  >
                    <Controller
                      name="secondaryContact.phoneNumber"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (!value) return true; // Allow empty values
                          const numericValue = value.replace(/\D/g, '');
                          if (numericValue.length > 4 && numericValue.length < 10)
                            return 'Phone number is too short';
                          if (numericValue.length > 15) return 'Phone number is too long';
                          return true;
                        },
                      }}
                      render={({ field: { onChange, value }, fieldState: { invalid } }) => (
                        <StyledPhoneInput className={invalid ? 'invalid' : ''}>
                          <PhoneInput defaultCountry="us" value={value} onChange={onChange} />
                        </StyledPhoneInput>
                      )}
                    />
                  </Form.Field>
                </div>
                <Form.Field labelText="Secondary Contact Photo" id="secondary-contact-photo">
                  <UploadImageInput
                    type="file"
                    id="secondary-contact-logo-upload"
                    onChange={(e) => onLogoUpload(e, 'secondary')}
                  />
                  {secondaryPhoto ? (
                    <>
                      <UploadedImageContainer>
                        <UploadedImage src={secondaryPhoto} alt="Secondary Contact" />
                      </UploadedImageContainer>
                      <ChangeImageLabel htmlFor="secondary-contact-logo-upload">
                        <ChangeImageButton>Change Image</ChangeImageButton>
                      </ChangeImageLabel>
                    </>
                  ) : (
                    <UploadImageLabel htmlFor="secondary-contact-logo-upload">
                      <UploadImageContainer>
                        <Flex column alignItemsCenter>
                          <Icon name="image" size="30px" color="gray400" />
                          <Body variant="Body/Body Small" color="gray400">
                            Upload Image
                          </Body>
                        </Flex>
                      </UploadImageContainer>
                    </UploadImageLabel>
                  )}
                </Form.Field>
              </Flex.Item>
            </Flex>
          </Form.FieldGroup>
        </div>
        <Divider />
        <Form.Footer justifyEnd>
          <PrimaryButton type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save'}
          </PrimaryButton>
        </Form.Footer>
      </Form>
    </SettingsMain>
  );
};

export default BrandSettings;
